import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import {
  Search,
  Loader2,
  CheckCircle2,
  Clock,
  MapPin,
  User,
  Home,
  AlertCircle,
  Save,
  ClipboardList,
  RefreshCw,
  Cloud,
  Trash2,
  Edit,
  ArrowLeft,
  ChevronRight,
} from 'lucide-react';
import { SiteSurveyFormOptimized as SiteSurveyForm } from '@/components/site-survey/site-survey-form-optimized';
import { SurveyCompletionDialog } from '@/components/site-survey/survey-completion-dialog';
import { PageLayout } from '@/components/ui/page-layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import {
  transformInstallationApiToUI,
  extractAutopopulationData,
  transformFormDataForApi,
  SITE_SURVEY_PHOTO_FIELDS,
  type InstallationUI,
} from '@/lib/transformers/site-survey';
import { uploadQueuedSurveyPhotos } from '@/lib/site-survey-upload';
import { useDebounce } from '@/hooks/use-debounce';
import { useOfflineStorage } from '@/hooks/use-offline-storage';
import { useRealtimeSync } from '@/hooks/use-realtime-sync';

// Helper function to format address consistently
function formatInstallationAddress(installation: InstallationUI): string {
  // Priority: 1. Customer address, 2. Installation address
  const primaryAddress = installation.customer?.address || installation.installationAddress || '';
  const postcode = installation.customer?.postcode || '';

  // If we have both address and postcode, ensure they're combined properly
  if (primaryAddress && postcode) {
    // Check if postcode is already in the address
    if (primaryAddress.toLowerCase().includes(postcode.toLowerCase())) {
      return primaryAddress;
    }
    // Add postcode if not already present
    return `${primaryAddress}, ${postcode}`;
  }

  // Return whatever we have
  return primaryAddress || postcode || 'No address provided';
}

export default function SiteSurveyPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  // A save keeps the survey even when the surveyor's calendar refused the
  // booking (a clash, or a calendar that could not be read); say so.
  const warnIfNotOnCalendar = useCallback(
    (response: any) => {
      const booking = response?.calendarBooking;
      if (booking && !booking.success && !booking.skipped) {
        toast({
          title: "Not added to the surveyor's calendar",
          description: booking.error || 'The calendar booking failed.',
          variant: 'destructive',
        });
      }
    },
    [toast]
  );
  const [selectedInstallation, setSelectedInstallation] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [savedSurveyId, setSavedSurveyId] = useState<number | null>(null);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [completedSurveyData, setCompletedSurveyData] = useState<{
    id: number;
    customerName: string;
    installationAddress: string;
  } | null>(null);
  const [autoPopulatedData, setAutoPopulatedData] = useState<ReturnType<
    typeof extractAutopopulationData
  > | null>(null);
  
  // Edit mode state
  const [editMode, setEditMode] = useState<{ surveyId: number; customerName?: string } | null>(null);
  const [isLoadingEditSurvey, setIsLoadingEditSurvey] = useState(false);

  // Auto-save state
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>(
    'idle'
  );
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [lastUserActivity, setLastUserActivity] = useState<Date>(new Date());
  // When this tab last had to keep the survey on the device (no signal).
  const offlineSavedAtRef = useRef<string | null>(null);
  // The office copy's updatedAt as this tab last saw it; recorded with offline saves
  // so the uploader can tell if someone else changed the survey in the meantime.
  const serverUpdatedAtRef = useRef<string | null | undefined>(undefined);
  // Whether the device copy for the open survey was there before this tab saved offline.
  const copyFromEarlierVisitRef = useRef(false);
  // Customer whose device copy this tab will upload itself (the queue skips it meanwhile).
  const claimedCustomerRef = useRef<number | null>(null);
  const [hasImageLoadError, setHasImageLoadError] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);

  // Detect edit mode from URL parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const editSurveyId = params.get('edit');
    
    if (editSurveyId && !isNaN(Number(editSurveyId))) {
      console.log('[SiteSurvey] Edit mode detected for survey:', editSurveyId);
      setEditMode({ surveyId: Number(editSurveyId) });
    }
  }, []);

  // Function to clear photo caches and reload
  const clearPhotoCaches = async () => {
    setClearingCache(true);
    try {
      // Clear React Query cache for site surveys
      await queryClient.invalidateQueries({ queryKey: ['site-survey'] });
      await queryClient.invalidateQueries({ queryKey: ['/api/site-surveys'] });
      if (selectedInstallation) {
        await queryClient.invalidateQueries({
          queryKey: [`/api/site-surveys/installation/${selectedInstallation}`],
        });
      }
      await queryClient.resetQueries();

      // Clear any cached photo URLs from localStorage
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('photo') || key.includes('image') || key.includes('survey'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));

      // Clear browser image caches if available
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(async (cacheName) => {
            const cache = await caches.open(cacheName);
            const requests = await cache.keys();

            // Delete cached image requests
            await Promise.all(
              requests.map(async (request) => {
                const url = request.url;
                if (
                  url.includes('/photos/') ||
                  url.includes('/api/site-surveys/photos/') ||
                  url.includes('/api/object-storage/') ||
                  url.includes('.jpg') ||
                  url.includes('.jpeg') ||
                  url.includes('.png')
                ) {
                  await cache.delete(request);
                }
              })
            );
          })
        );
      }

      toast({
        title: 'Cache Cleared',
        description: 'Photo cache has been cleared. The page will reload.',
      });

      // Reload after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Error clearing cache:', error);
      toast({
        title: 'Cache Clear Failed',
        description:
          'There was an error clearing the cache. Please try refreshing the page manually.',
        variant: 'destructive',
      });
    } finally {
      setClearingCache(false);
    }
  };

  // Offline storage hook
  const {
    isOnline,
    storageProblem,
    saveSurvey: saveOffline,
    getUploadState,
    markSavedToOffice,
    uploadMyVersionAnyway,
    claim,
    release,
  } = useOfflineStorage();

  // Fetch installations from Won stage only for site survey
  const {
    data: installationsData,
    isLoading: isLoadingInstallations,
    error,
    refetch: refetchInstallations,
  } = useQuery({
    queryKey: ['/api/installations', 'forSiteSurvey', 'v5'], // Added v5 to force cache refresh after fixes
    queryFn: async () => {
      const response = await fetch(`/api/installations?forSiteSurvey=true&_t=${Date.now()}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('[SiteSurvey] Fetched installations for site survey:', data.length, 'customers');
      // Fetched Won customers (excluding those with completed surveys)
      return data.map(transformInstallationApiToUI);
    },
    staleTime: 0, // Always fetch fresh data
    gcTime: 0, // Don't cache (garbage collection time)
  });

  const installations = useMemo<InstallationUI[]>(() => installationsData || [], [installationsData]);

  // Helper function to transform survey data with photos
  const transformSurveyWithPhotos = useCallback((survey: any) => {
    if (!survey) return survey;

    const transformedSurvey = { ...survey };

    // If photos are in a separate photos object, unpack them to individual fields
    if (survey.photos && typeof survey.photos === 'object') {
      // Map photos from the photos object to individual fields
      SITE_SURVEY_PHOTO_FIELDS.forEach((field) => {
        if (survey.photos[field]) {
          // Extract URL from photo object or use the value directly if it's already a URL string
          if (typeof survey.photos[field] === 'object' && survey.photos[field].url) {
            transformedSurvey[field] = survey.photos[field].url;
            console.log(
              `[SiteSurvey] Setting photo field ${field} to URL:`,
              survey.photos[field].url
            );
          } else if (typeof survey.photos[field] === 'string') {
            transformedSurvey[field] = survey.photos[field];
            console.log(
              `[SiteSurvey] Setting photo field ${field} to string value:`,
              survey.photos[field]
            );
          }
        }
      });
    }

    return transformedSurvey;
  }, []);

  // Function to load survey for editing
  const loadSurveyForEdit = useCallback(
    async (surveyId: number) => {
      setIsLoadingEditSurvey(true);
      try {
        console.log('[SiteSurvey] Loading survey for editing:', surveyId);
        
        // Try to fetch survey with customer context first
        let response;
        let survey;
        let customerFromContext = null;
        
        try {
          // Try the new edit-context endpoint first
          const contextResponse = await apiRequest({
            method: 'GET',
            url: `/api/site-surveys/${surveyId}/edit-context`,
          });
          
          survey = contextResponse.survey;
          customerFromContext = contextResponse.customer;
          console.log('[SiteSurvey] Loaded survey with customer context:', survey.id, customerFromContext);
        } catch (error) {
          // Fallback to regular endpoint
          console.log('[SiteSurvey] Edit context endpoint failed, using regular endpoint');
          response = await apiRequest({
            method: 'GET',
            url: `/api/site-surveys/${surveyId}`,
          });
          survey = response?.survey || response;
        }
        console.log('[SiteSurvey] Loaded survey:', survey.id, 'for customer:', survey.customerId);
        
        // Find the associated installation (customer in Won stage)
        let installation = installations.find(
          (inst: InstallationUI) => 
            inst.customerId === survey.customerId || 
            inst.customer?.id === survey.customerId
        );
        
        // If customer is not in Won stage, use context customer or fetch directly
        if (!installation && survey.customerId) {
          console.log('[SiteSurvey] Customer not in Won stage, using context or fetching...');
          
          // Use customer from context if available
          let customerData = customerFromContext;
          
          // If no context customer, fetch directly
          if (!customerData) {
            try {
              const customerResponse = await apiRequest({
                method: 'GET',
                url: `/api/customers/${survey.customerId}`,
              });
              customerData = customerResponse;
            } catch (error) {
              console.error('[SiteSurvey] Failed to fetch customer:', error);
              // Continue anyway - the survey data itself may be enough
            }
          }
          
          if (customerData) {
            const customerRecord = customerData as Record<string, any>;
            const customerName =
              customerRecord.name ||
              [customerRecord.firstName, customerRecord.lastName].filter(Boolean).join(' ') ||
              survey.clientName ||
              'Unknown';

            // Create a pseudo-installation object for the form to work with
            installation = {
              id: Number(survey.customerId),
              customerId: Number(survey.customerId),
              customer: {
                id: Number(customerRecord.id || survey.customerId),
                name: customerName,
                email: customerRecord.email || survey.clientEmail,
                phone: customerRecord.phone || survey.clientTelephone,
                address:
                  customerRecord.address || survey.installationAddress || survey.clientAddress,
                postcode:
                  customerRecord.postcode ||
                  survey.installationPostcode ||
                  survey.clientPostCode,
              },
              installationAddress:
                customerRecord.address || survey.installationAddress || survey.clientAddress || '',
              systemSize: 0,
              leadOwner: survey.leadOwner || '',
            };
            
            console.log('[SiteSurvey] Created pseudo-installation for editing');
          }
        }
        
        if (installation) {
          console.log('[SiteSurvey] Found installation:', installation.id);
          
          // Set the selected installation
          setSelectedInstallation(
            typeof installation.id === 'number' ? installation.id : parseInt(String(installation.id), 10)
          );
          setSavedSurveyId(survey.id);
          
          // Update edit mode with customer name
          const customerName = installation.customer?.name || 'Unknown';
          setEditMode({ surveyId: survey.id, customerName });
          
          // Transform and load the survey data into form
          const transformedData = transformSurveyWithPhotos(survey);
          setFormData(transformedData);
          setAutoPopulatedData(transformedData);
          
          // Scroll to form after a short delay
          setTimeout(() => {
            const formElement = document.querySelector('.site-survey-form');
            if (formElement) {
              formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
          
          toast({
            title: 'Survey Loaded',
            description: `Editing survey for ${customerName}`,
          });
        } else {
          // No customer data available at all
          console.warn('[SiteSurvey] Could not load customer data for survey:', survey.customerId);
          
          // Still allow editing with just the survey data
          toast({
            title: 'Limited Customer Data',
            description: 'Some customer information may not be available, but you can still edit the survey.',
            variant: 'default',
          });
          
          // Set survey data for editing
          setSavedSurveyId(survey.id);
          const customerName = survey.clientName || 'Survey #' + survey.id;
          setEditMode({ surveyId: survey.id, customerName });
          
          // Load the survey data
          const transformedData = transformSurveyWithPhotos(survey);
          setFormData(transformedData);
          setAutoPopulatedData(transformedData);
          
          // The form will work with the survey data alone
        }
      } catch (error: any) {
        console.error('[SiteSurvey] Failed to load survey for editing:', error);
        
        const errorMessage = error?.response?.data?.error || error?.message || 'Failed to load survey';
        const isNotFound = error?.response?.status === 404;
        
        toast({
          title: isNotFound ? 'Survey Not Found' : 'Failed to Load Survey',
          description: isNotFound 
            ? `Survey #${surveyId} was not found. It may have been deleted.`
            : errorMessage,
          variant: 'destructive',
        });
        
        // Clear edit mode and optionally redirect
        setTimeout(() => {
          if (isNotFound) {
            // Redirect to survey management for not found
            window.location.href = '/survey-management';
          } else {
            // Just clear edit mode for other errors
            window.history.replaceState({}, '', '/site-surveys');
            setEditMode(null);
          }
        }, 3000);
      } finally {
        setIsLoadingEditSurvey(false);
      }
    },
    [toast, setSelectedInstallation, setSavedSurveyId, setFormData, setAutoPopulatedData, transformSurveyWithPhotos, installations]
  );

  // Load survey when in edit mode (don't wait for installations list)
  useEffect(() => {
    if (editMode && !isLoadingEditSurvey) {
      loadSurveyForEdit(editMode.surveyId);
    }
  }, [editMode?.surveyId]); // Only trigger when survey ID changes

  // Note: Background photo sync will be initialized after existingSurvey is loaded

  // Listen for image load errors globally (for developer debugging)
  useEffect(() => {
    const handleError = (e: ErrorEvent) => {
      if (e.target instanceof HTMLImageElement && e.target.src.includes('/photos/')) {
        console.error('[SiteSurveyPage] Image failed to load:', e.target.src);
        setHasImageLoadError(true);
      }
    };

    window.addEventListener('error', handleError, true);
    return () => window.removeEventListener('error', handleError, true);
  }, []);

  // Debounced form data for auto-save (5 seconds for periodic backup)
  const debouncedFormData = useDebounce(formData, 5000);

  // Get customer ID from selected installation
  const selectedCustomerId = useMemo(() => {
    if (!selectedInstallation || !installations?.length) return null;
    const installation = installations.find(
      (i: InstallationUI) => i.id === selectedInstallation || i.customerId === selectedInstallation
    );
    const customerId = installation?.customer?.id || installation?.customerId;
    console.log(
      '[SiteSurvey] Selected installation:',
      selectedInstallation,
      '-> Customer ID:',
      customerId
    );
    return customerId;
  }, [selectedInstallation, installations]);

  const selectedInstallationDetails = useMemo(() => {
    if (!selectedInstallation) return null;
    return (
      installations.find(
        (inst: InstallationUI) =>
          inst.id === selectedInstallation || inst.customerId === selectedInstallation
      ) || null
    );
  }, [selectedInstallation, installations]);

  const selectedActualInstallationId = useMemo(() => {
    if (!selectedInstallationDetails || selectedInstallationDetails.isCustomerOnly) return null;
    const installationId = Number(selectedInstallationDetails.id);
    return Number.isFinite(installationId) ? installationId : null;
  }, [selectedInstallationDetails]);

  // Fetch existing survey for any customer/installation
  const { data: existingSurvey, refetch: refetchSurvey } = useQuery({
    queryKey: ['site-survey', selectedCustomerId],
    queryFn: async () => {
      if (!selectedCustomerId) {
        console.log('[SiteSurvey] No customer ID available, skipping survey fetch');
        return null;
      }

      console.log('[SiteSurvey] Fetching survey for customer ID:', selectedCustomerId);

      try {
        // Use customer endpoint with actual customer ID
        const response = await apiRequest({
          method: 'GET',
          url: `/api/site-surveys/customer/${selectedCustomerId}?optional=true`,
        });
        // Extract survey data from response wrapper
        const survey = response?.survey || response;
        console.log('[SiteSurvey] Found existing survey:', survey?.id, 'status:', survey?.status);
        return survey;
      } catch (error: any) {
        // If survey doesn't exist, return null (not an error)
        if (error.status === 404) {
          console.log('[SiteSurvey] No existing survey found for customer:', selectedCustomerId);
          return null;
        }
        console.error('[SiteSurvey] Error fetching survey:', error);
        throw error;
      }
    },
    enabled: !!selectedCustomerId,
  });

  // Background photo sync - runs silently with no UI
  const surveyIdForSync = savedSurveyId || existingSurvey?.id;
  useRealtimeSync(surveyIdForSync);

  useEffect(() => {
    serverUpdatedAtRef.current = undefined;
    offlineSavedAtRef.current = null;
    copyFromEarlierVisitRef.current = false;
    // Leaving this survey (or the page) hands any copy back to the background queue.
    return () => {
      if (claimedCustomerRef.current !== null) {
        release(claimedCustomerRef.current);
        claimedCustomerRef.current = null;
      }
    };
  }, [selectedCustomerId, release]);

  useEffect(() => {
    if (existingSurvey === undefined) return;
    serverUpdatedAtRef.current = existingSurvey?.updatedAt
      ? new Date(existingSurvey.updatedAt).toISOString()
      : null;
  }, [existingSurvey]);

  const uploadState = getUploadState(selectedCustomerId);

  // A copy left on the device by an earlier visit means the form opened with the
  // older office version. Once the background upload clears it, show the office
  // copy (unless staff are mid-edit; their next save carries the changes). Copies
  // this tab made itself are already in the form, so those never trigger a reload.
  const previousUploadStateRef = useRef(uploadState.state);
  useEffect(() => {
    const previous = previousUploadStateRef.current;
    previousUploadStateRef.current = uploadState.state;
    if (uploadState.state !== 'none' && offlineSavedAtRef.current === null) {
      copyFromEarlierVisitRef.current = true;
    }
    if (previous !== 'none' && uploadState.state === 'none') {
      if (copyFromEarlierVisitRef.current && !unsavedChanges) void refetchSurvey();
      copyFromEarlierVisitRef.current = false;
    }
  }, [uploadState.state, unsavedChanges, refetchSurvey]);

  // Handle installation selection
  const handleInstallationSelect = (installationId: number | string) => {
    // Warn about unsaved changes
    if (unsavedChanges && selectedInstallation) {
      if (!confirm('You have unsaved changes. Do you want to continue?')) {
        return;
      }
    }

    // For site survey mode, the ID is the customer ID
    const numericId =
      typeof installationId === 'number' ? installationId : parseInt(installationId);

    setSelectedInstallation(numericId);
    setSavedSurveyId(null);
    setUnsavedChanges(false);
    setAutoSaveStatus('idle');

    const installation = installations.find(
      (inst: InstallationUI) => inst.id === numericId || inst.customerId === numericId
    );

    if (installation) {
      const populatedData = extractAutopopulationData(installation);
      // Auto-populated data extracted
      setAutoPopulatedData(populatedData);
    }

    // Scroll to the form after a short delay to ensure it's rendered
    setTimeout(() => {
      const formElement = document.getElementById('site-survey-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Form change handler
  const handleFormChange = useCallback((data: Record<string, any>) => {
    setFormData(data);
    setUnsavedChanges(true);
    setLastUserActivity(new Date());
  }, []);

  // Verify actual connectivity with API ping
  const verifyOnlineStatus = useCallback(async (): Promise<boolean> => {
    // If browser says offline, don't bother checking
    if (!navigator.onLine) {
      return false;
    }

    // Quick API ping to verify actual connectivity
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 2000);

      const response = await fetch('/api/health', {
        method: 'HEAD',
        signal: controller.signal,
        cache: 'no-cache',
        credentials: 'include',
      });

      clearTimeout(timeout);
      return response.ok;
    } catch (error) {
      console.log('[SiteSurvey] Connectivity check failed:', error);
      return false;
    }
  }, []);

  const uploadQueuedPhotosForSurvey = useCallback(
    async ({
      surveyId,
      data,
      customerId,
      installationId,
      silent = false,
    }: {
      surveyId: number;
      data: Record<string, any>;
      customerId?: number | null;
      installationId?: number | null;
      silent?: boolean;
    }): Promise<{
      data: Record<string, any>;
      uploadedCount: number;
      failedCount: number;
    }> => {
      const { data: updatedData, uploadedCount, failures } = await uploadQueuedSurveyPhotos({
        surveyId,
        data,
        customerId,
        installationId,
      });

      if (uploadedCount > 0) {
        setFormData(updatedData);
      }

      if (failures.length > 0 && !silent) {
        toast({
          title: 'Photo Upload Failed',
          description:
            failures.length === 1
              ? failures[0]
              : `${failures.length} queued photos could not be uploaded. Save again to retry.`,
          variant: 'destructive',
        });
      }

      return {
        data: updatedData,
        uploadedCount,
        failedCount: failures.length,
      };
    },
    [toast]
  );

  // No signal: keep the survey on this device. The offline queue uploads it
  // automatically once the device is back online.
  const keepOnDevice = useCallback(
    (customerId: number, data: Record<string, any>) => {
      const firstOfflineSave = offlineSavedAtRef.current === null;
      const toKeep =
        existingSurvey?.status === 'completed' ? { ...data, status: 'completed' } : data;
      saveOffline(
        customerId,
        selectedActualInstallationId || undefined,
        savedSurveyId || existingSurvey?.id || undefined,
        toKeep,
        serverUpdatedAtRef.current
      );
      offlineSavedAtRef.current = new Date().toISOString();
      // This tab's form holds the copy and saves it on reconnect; keep the queue off it.
      claim(customerId);
      claimedCustomerRef.current = customerId;
      return firstOfflineSave;
    },
    [
      existingSurvey?.status,
      existingSurvey?.id,
      saveOffline,
      selectedActualInstallationId,
      savedSurveyId,
      claim,
    ]
  );

  // After a save reaches the office, drop the copy kept on this device if this
  // tab's form already contained it.
  const settleDeviceCopy = useCallback(
    (
      customerId: number,
      response: any,
      /** What was saved, with failed photos still inline; null when every photo uploaded. */
      dataWithPendingPhotos: Record<string, any> | null
    ) => {
      const updatedAt = response?.survey?.updatedAt;
      if (updatedAt) serverUpdatedAtRef.current = new Date(updatedAt).toISOString();
      if (offlineSavedAtRef.current) {
        markSavedToOffice(
          customerId,
          offlineSavedAtRef.current,
          serverUpdatedAtRef.current ?? null,
          dataWithPendingPhotos,
          response?.surveyId || response?.survey?.id
        );
        if (!dataWithPendingPhotos) {
          offlineSavedAtRef.current = null;
          if (claimedCustomerRef.current !== null) {
            release(claimedCustomerRef.current);
            claimedCustomerRef.current = null;
          }
        }
      }
    },
    [markSavedToOffice, release]
  );

  // Auto-save function (reusable for different triggers)
  const performAutoSave = useCallback(
    async (reason?: string) => {
      // 'reconnect' sends the copy this tab kept on the device, even with no new edits.
      if (
        !selectedInstallation ||
        (!unsavedChanges && reason !== 'reconnect') ||
        Object.keys(formData).length === 0
      ) {
        return;
      }

      // Don't auto-save if we're already saving
      if (autoSaveStatus === 'saving') {
        return;
      }

      // Auto-saving...
      setAutoSaveStatus('saving');

      try {
        // Ensure we have at least some data to save
        // Remove overly restrictive validation - allow saving draft surveys with partial data
        if (Object.keys(formData).length === 0) {
          // Skipping auto-save: no form data
          setAutoSaveStatus('idle');
          return;
        }

        // Use the same customer ID logic as the query
        const installation = installations?.find(
          (i: InstallationUI) => i.id === selectedInstallation || i.customerId === selectedInstallation
        );
        const customerId =
          installation?.customer?.id || installation?.customerId || formData.customerId;

        // Verify actual connectivity before deciding offline mode
        const actuallyOnline = isOnline && await verifyOnlineStatus();

        // If offline, keep it on this device
        if (!actuallyOnline) {
          const firstOfflineSave = keepOnDevice(customerId, formData);
          setAutoSaveStatus('saved');
          setLastSaveTime(new Date());
          setUnsavedChanges(false);

          if (firstOfflineSave) {
            toast({
              title: 'No signal',
              description:
                "Saved on this device. It will upload to the office automatically when you're back online.",
            });
          }

          setTimeout(() => setAutoSaveStatus('idle'), 3000);
          return;
        }

        // Online save - simple data preparation
        // IMPORTANT: For auto-save, preserve completed status if survey is already completed
        const dataToTransform: any = {
          ...formData,
          customerId: customerId, // Use any available customer ID
          installationId: selectedActualInstallationId,
        };

        // If existing survey is completed, explicitly maintain that status
        if (existingSurvey?.status === 'completed') {
          dataToTransform.status = 'completed';
          console.log('[SiteSurvey] Auto-save: preserving completed status');
        }
        // Otherwise don't set status - let backend preserve existing status

        const transformedData = transformFormDataForApi(dataToTransform);

        const response = await apiRequest({
          url: '/api/site-surveys/save',
          method: 'POST',
          data: transformedData,
        });
        warnIfNotOnCalendar(response);

        // Handle both direct surveyId and wrapped response
        const surveyId = response?.surveyId || response?.survey?.id;
        let finalResponse = response;
        let queuedPhotoFailures = 0;

        if (surveyId) {
          setSavedSurveyId(surveyId);
          const queuedPhotoResult = await uploadQueuedPhotosForSurvey({
            surveyId,
            data: dataToTransform,
            customerId,
            installationId: selectedActualInstallationId,
            silent: reason !== 'manual' && reason !== 'tab-change',
          });
          queuedPhotoFailures = queuedPhotoResult.failedCount;

          if (queuedPhotoResult.uploadedCount > 0) {
            finalResponse = await apiRequest({
              url: '/api/site-surveys/save',
              method: 'POST',
              data: transformFormDataForApi(queuedPhotoResult.data),
            });
          }

          settleDeviceCopy(
            customerId,
            finalResponse,
            queuedPhotoFailures > 0 ? queuedPhotoResult.data : null
          );
        }

        setAutoSaveStatus('saved');
        setLastSaveTime(new Date());
        setUnsavedChanges(queuedPhotoFailures > 0);

        // Show success toast only for manual saves
        if (reason === 'manual' || reason === 'tab-change') {
          toast({
            title: 'Survey Saved',
            description: 'All changes saved successfully to server.',
          });
        }

        // Reset status after 3 seconds
        setTimeout(() => setAutoSaveStatus('idle'), 3000);

        // Don't invalidate queries during auto-save to prevent form reset
        // queryClient.invalidateQueries({ queryKey: ['site-surveys'] });
        // queryClient.invalidateQueries({ queryKey: ['site-survey', selectedInstallation] });
      } catch (error: any) {
        // Verify actual connectivity before entering offline mode
        const actuallyOnline = isOnline && await verifyOnlineStatus();

        // If network error or truly offline, save offline
        if (
          error?.code === 'NETWORK_ERROR' ||
          error?.message?.includes('network') ||
          !actuallyOnline
        ) {
          const installation = installations?.find(
            (inst) => inst.id === selectedInstallation || inst.customerId === selectedInstallation
          );
          const customerId =
            installation?.customer?.id || installation?.customerId || formData.customerId;

          const firstOfflineSave = keepOnDevice(customerId, formData);
          setAutoSaveStatus('saved');
          setLastSaveTime(new Date());
          setUnsavedChanges(false);

          if (firstOfflineSave) {
            toast({
              title: 'Signal lost',
              description:
                "Saved on this device. It will upload to the office automatically when you're back online.",
            });
          }

          setTimeout(() => setAutoSaveStatus('idle'), 3000);
          return;
        }

        setAutoSaveStatus('error');
        console.error('Auto-save failed:', error);

        const is401 = error?.message?.includes('401');

        // On 401, preserve form data in localStorage so user doesn't lose work
        if (is401 && formData) {
          try {
            localStorage.setItem('siteSurvey_unsavedData', JSON.stringify({
              data: formData,
              installationId: selectedActualInstallationId,
              savedAt: new Date().toISOString(),
            }));
            console.log('[SiteSurvey] Auto-save 401: form data preserved in localStorage');
          } catch (storageError) {
            console.warn('[SiteSurvey] Could not preserve form data:', storageError);
          }
        }

        // Only show toast for manual saves, tab changes, and session expiry
        if (is401) {
          toast({
            title: 'Session Expired',
            description: 'Your session has expired. Your work has been preserved. Please log in again.',
            variant: 'destructive',
          });
        } else if (
          reason === 'manual' ||
          (reason === 'tab-change' && !error?.message?.includes('400'))
        ) {
          toast({
            title: 'Save Failed',
            description: "Your changes couldn't be saved. Please try again.",
            variant: 'destructive',
          });
        }

        // Reset error status after 5 seconds
        setTimeout(() => setAutoSaveStatus('idle'), 5000);
      }
    },
    [
      formData,
      selectedInstallation,
      unsavedChanges,
      queryClient,
      toast,
      autoSaveStatus,
      isOnline,
      keepOnDevice,
      settleDeviceCopy,
      installations,
      savedSurveyId,
      verifyOnlineStatus,
      existingSurvey?.status,
      uploadQueuedPhotosForSurvey,
      selectedActualInstallationId,
      warnIfNotOnCalendar,
    ]
  );

  // Handle tab changes with auto-save
  const handleTabChange = useCallback(
    (newTab: string) => {
      if (unsavedChanges) {
        performAutoSave('tab-change');
      }
    },
    [unsavedChanges, performAutoSave]
  );

  // Handle photo upload completion - mark as unsaved but don't auto-save immediately
  const handlePhotoUploaded = useCallback(() => {
    // Just mark that we have unsaved changes, don't trigger auto-save
    // This prevents annoying error toasts when uploading photos
    setUnsavedChanges(true);
    setLastUserActivity(new Date());
  }, []);

  // Periodic auto-save effect (30 seconds, only if user active)
  useEffect(() => {
    if (!selectedInstallation || !unsavedChanges || Object.keys(debouncedFormData).length === 0) {
      return;
    }

    // Check if user has been active in the last 60 seconds
    const timeSinceActivity = new Date().getTime() - lastUserActivity.getTime();
    const isUserActive = timeSinceActivity < 60000; // 1 minute

    if (isUserActive) {
      performAutoSave('periodic');
    }
  }, [debouncedFormData, selectedInstallation, unsavedChanges, lastUserActivity, performAutoSave]);

  // Additional auto-save trigger on explicit interval (every 30 seconds if there are changes)
  useEffect(() => {
    if (!selectedInstallation || !unsavedChanges) {
      return;
    }

    const interval = setInterval(() => {
      if (unsavedChanges && autoSaveStatus !== 'saving') {
        performAutoSave('interval');
      }
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [selectedInstallation, unsavedChanges, autoSaveStatus, performAutoSave]);

  // When signal returns, send the copy this tab kept on the device. The browser's
  // 'online' event is unreliable on phones, so also check every 30 seconds.
  const performAutoSaveRef = useRef(performAutoSave);
  performAutoSaveRef.current = performAutoSave;
  useEffect(() => {
    if (!selectedInstallation) return;
    const sendDeviceCopy = () => {
      if (offlineSavedAtRef.current && navigator.onLine) {
        void performAutoSaveRef.current('reconnect');
      }
    };
    window.addEventListener('online', sendDeviceCopy);
    const interval = setInterval(sendDeviceCopy, 30000);
    return () => {
      window.removeEventListener('online', sendDeviceCopy);
      clearInterval(interval);
    };
  }, [selectedInstallation]);

  // Save on page unload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (unsavedChanges) {
        performAutoSave('page-unload');
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [unsavedChanges, performAutoSave]);

  // Enhanced save survey mutation with improved error handling
  const saveSurveyMutation = useMutation({
    mutationFn: async (data: Record<string, any>) => {
      // Enhanced customer ID resolution with validation
      const installation = installations?.find(
        (i: InstallationUI) => i.id === selectedInstallation || i.customerId === selectedInstallation
      );
      let customerId = installation?.customer?.id || installation?.customerId || data.customerId;

      // Validate customer ID exists
      if (!customerId) {
        throw new Error('Customer ID not found. Please ensure a valid installation is selected.');
      }

      console.log(
        '[SiteSurvey] Save mutation - installation:',
        selectedInstallation,
        'customer:',
        customerId
      );

      // Enhanced data preparation - preserve existing status if survey is already completed
      const dataToTransform: any = {
        ...data,
        customerId: customerId,
        installationId: selectedActualInstallationId,
        // Only set status if explicitly provided, otherwise let backend preserve it
        ...(data.status ? { status: data.status } : {}),
        // Add last save timestamp
        lastSavedAt: new Date().toISOString(),
      };

      const transformedData = transformFormDataForApi(dataToTransform);

      console.log('[SiteSurvey] Saving survey with enhanced data:', transformedData);

      // Add retry logic for save operations
      let attempt = 0;
      const maxAttempts = 2;

      while (attempt < maxAttempts) {
        try {
          const response = await apiRequest({
            url: '/api/site-surveys/save',
            method: 'POST',
            data: transformedData,
            headers: {
              'X-Request-ID': `survey-save-${Date.now()}-${attempt}`,
              'X-Survey-Operation': 'save',
            },
          });
          warnIfNotOnCalendar(response);
          let finalResponse = response;
          let queuedPhotoUploadFailures = 0;
          let savedData: Record<string, any> = dataToTransform;
          const surveyId = response?.surveyId || response?.survey?.id;

          if (surveyId) {
            const queuedPhotoResult = await uploadQueuedPhotosForSurvey({
              surveyId,
              data: dataToTransform,
              customerId,
              installationId: selectedActualInstallationId,
            });
            queuedPhotoUploadFailures = queuedPhotoResult.failedCount;
            savedData = queuedPhotoResult.data;

            if (queuedPhotoResult.uploadedCount > 0) {
              finalResponse = await apiRequest({
                url: '/api/site-surveys/save',
                method: 'POST',
                data: transformFormDataForApi(queuedPhotoResult.data),
                headers: {
                  'X-Request-ID': `survey-save-photo-finalize-${Date.now()}-${attempt}`,
                  'X-Survey-Operation': 'save-photo-finalize',
                },
              });
            }
          }

          // Clear any previously preserved form data on successful save
          try {
            localStorage.removeItem('siteSurvey_unsavedData');
          } catch (storageError) {
            console.warn('[SiteSurvey] Could not clear preserved form data:', storageError);
          }
          settleDeviceCopy(
            customerId,
            finalResponse,
            queuedPhotoUploadFailures > 0 ? savedData : null
          );
          return {
            ...finalResponse,
            queuedPhotoUploadFailures,
          };
        } catch (error: any) {
          attempt++;
          const is401 = error?.message?.includes('401');
          console.warn(`[SiteSurvey] Save attempt ${attempt} failed:`, error?.response?.data);

          // On 401, preserve form data in localStorage so user doesn't lose work
          if (is401) {
            try {
              localStorage.setItem('siteSurvey_unsavedData', JSON.stringify({
                data: dataToTransform,
                installationId: selectedActualInstallationId,
                savedAt: new Date().toISOString(),
              }));
              console.log('[SiteSurvey] Form data preserved in localStorage for session recovery');
            } catch (storageError) {
              console.warn('[SiteSurvey] Could not preserve form data:', storageError);
            }
            // Don't retry on 401 — session is expired, retrying won't help
            throw error;
          }

          // If this is the last attempt or it's a non-retryable error, throw
          const is400 = error?.message?.includes('400');
          const is404 = error?.message?.includes('404');
          if (attempt >= maxAttempts || is400 || is404) {
            throw error;
          }

          // Short wait before retry for saves
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }
    },
    onSuccess: (data) => {
      // Handle both direct surveyId and wrapped response
      const surveyId = data?.surveyId || data?.survey?.id;
      if (surveyId) {
        setSavedSurveyId(surveyId);
      }
      setUnsavedChanges((data?.queuedPhotoUploadFailures || 0) > 0);
      setAutoSaveStatus('saved');
      setLastSaveTime(new Date());

      toast({
        title: 'Survey Saved',
        description: 'Your changes have been saved successfully.',
      });

      queryClient.invalidateQueries({ queryKey: ['site-surveys'] });
      queryClient.invalidateQueries({ queryKey: ['site-survey', selectedCustomerId] });
    },
    onError: (error: any) => {
      console.error('[SiteSurvey] Save survey error:', error);
      console.error('[SiteSurvey] Error response:', error.response);
      console.error('[SiteSurvey] Error data:', error.response?.data);

      // Handle 401 specifically — session expired
      if (error?.message?.includes('401')) {
        // Check if localStorage preservation actually succeeded
        let savedLocally = false;
        try {
          savedLocally = localStorage.getItem('siteSurvey_unsavedData') !== null;
        } catch (storageError) {
          console.warn('[SiteSurvey] Could not check preserved form data:', storageError);
        }
        toast({
          title: 'Session Expired',
          description: savedLocally
            ? 'Your session has expired. Your work has been preserved locally. Please log in again.'
            : 'Your session has expired. Please log in again. Some unsaved changes may be lost.',
          variant: 'destructive',
        });
        return;
      }

      const errorData = error.response?.data;
      let title = 'Save Failed';
      let description = 'Failed to save survey';

      // Handle enhanced error responses
      if (errorData?.code) {
        switch (errorData.code) {
          case 'VALIDATION_ERROR':
            title = 'Validation Error';
            description = errorData.details?.fields
              ? `Missing fields: ${errorData.details.fields.join(', ')}`
              : errorData.message || 'Some required fields are missing';
            break;
          case 'MISSING_INSTALLATION':
            title = 'Installation Error';
            description = 'Installation not found. Please select a valid installation.';
            break;
          case 'MISSING_CUSTOMER':
            title = 'Customer Error';
            description = 'Customer not found. Please select a valid customer.';
            break;
          default:
            description = errorData.error || errorData.message || 'Failed to save survey';
        }
      } else if (error.message) {
        description = error.message;
      } else {
        description = errorData?.error || errorData?.message || 'Failed to save survey';
      }

      toast({
        title,
        description,
        variant: 'destructive',
      });
    },
  });

  // Complete survey mutation with enhanced error handling and retry logic
  const completeSurveyMutation = useMutation({
    mutationFn: async (data: Record<string, any>) => {
      // Enhanced customer ID resolution with better error handling
      const installation = installations?.find(
        (i: InstallationUI) => i.id === selectedInstallation || i.customerId === selectedInstallation
      );
      let customerId = installation?.customer?.id || installation?.customerId || data.customerId;

      // Validate customer ID exists
      if (!customerId) {
        throw new Error('Customer ID not found. Please ensure a valid installation is selected.');
      }

      console.log(
        '[SiteSurvey] Complete mutation - installation:',
        selectedInstallation,
        'customer:',
        customerId
      );

      // Enhanced data preparation with validation
      const dataToTransform: any = {
        ...data,
        customerId: customerId,
        installationId: selectedActualInstallationId,
        status: 'completed',
        // Add completion timestamp
        completedAt: new Date().toISOString(),
      };

      // Clear any stale cache entries before transformation
      await queryClient.invalidateQueries({ queryKey: ['site-surveys'] });
      await queryClient.invalidateQueries({ queryKey: ['site-survey', customerId] });

      const transformedData = transformFormDataForApi(dataToTransform);

      console.log('[SiteSurvey] Completing survey with enhanced data:', transformedData);

      // Retry logic for API calls
      let attempt = 0;
      const maxAttempts = 3;

      while (attempt < maxAttempts) {
        try {
          const response = await apiRequest({
            url: '/api/site-surveys/save',
            method: 'POST',
            data: transformedData,
            headers: {
              'X-Request-ID': `survey-complete-${Date.now()}-${attempt}`,
              'X-Survey-Operation': 'complete',
            },
          });
          warnIfNotOnCalendar(response);
          let finalResponse = response;
          let queuedPhotoUploadFailures = 0;
          let savedData: Record<string, any> = dataToTransform;
          const surveyId = response?.surveyId || response?.survey?.id;

          if (surveyId) {
            const queuedPhotoResult = await uploadQueuedPhotosForSurvey({
              surveyId,
              data: dataToTransform,
              customerId,
              installationId: selectedActualInstallationId,
            });
            queuedPhotoUploadFailures = queuedPhotoResult.failedCount;
            savedData = queuedPhotoResult.data;

            if (queuedPhotoResult.uploadedCount > 0) {
              finalResponse = await apiRequest({
                url: '/api/site-surveys/save',
                method: 'POST',
                data: transformFormDataForApi({
                  ...queuedPhotoResult.data,
                  status: 'completed',
                }),
                headers: {
                  'X-Request-ID': `survey-complete-photo-finalize-${Date.now()}-${attempt}`,
                  'X-Survey-Operation': 'complete-photo-finalize',
                },
              });
            }
          }

          settleDeviceCopy(
            customerId,
            finalResponse,
            queuedPhotoUploadFailures > 0 ? savedData : null
          );
          return {
            ...finalResponse,
            queuedPhotoUploadFailures,
          };
        } catch (error: any) {
          attempt++;
          console.warn(`[SiteSurvey] Complete attempt ${attempt} failed:`, error?.response?.data);

          // If this is the last attempt or it's a non-retryable error, throw
          if (
            attempt >= maxAttempts ||
            error?.response?.status === 400 ||
            error?.response?.status === 404
          ) {
            throw error;
          }

          // Wait before retry (exponential backoff)
          await new Promise((resolve) => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        }
      }
    },
    onSuccess: (data) => {
      const installation = installations.find(
        (inst: InstallationUI) =>
          inst.id === selectedInstallation || inst.customerId === selectedInstallation
      );
      setCompletedSurveyData({
        id: data?.surveyId || savedSurveyId || 0,
        customerName: installation?.customer?.name || 'Unknown Customer',
        installationAddress: installation?.installationAddress || 'Unknown Address',
      });
      setShowCompletionDialog(true);
      const hasPhotoUploadFailures = (data?.queuedPhotoUploadFailures || 0) > 0;
      setUnsavedChanges(hasPhotoUploadFailures);

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['site-surveys'] });
      queryClient.invalidateQueries({ queryKey: ['site-survey', selectedCustomerId] });

      // IMPORTANT: Refresh the installations list to remove completed survey from Site Survey tab
      // This will trigger the forSiteSurvey query to re-fetch and exclude the now-completed survey
      queryClient.invalidateQueries({ queryKey: ['/api/installations', 'forSiteSurvey', 'v5'] });
      refetchInstallations();

      if (!hasPhotoUploadFailures) {
        // Clear the selected installation after completion
        setTimeout(() => {
          setSelectedInstallation(null);
        }, 2000); // Delay to allow user to see the completion dialog
      }
    },
    onError: (error: any) => {
      console.error('[SiteSurvey] Complete survey error:', error);
      console.error('[SiteSurvey] Error response:', error.response);
      console.error('[SiteSurvey] Error data:', error.response?.data);

      const errorData = error.response?.data;
      let title = 'Completion Failed';
      let description = 'Failed to complete survey';

      // Handle enhanced error responses
      if (errorData?.code) {
        switch (errorData.code) {
          case 'VALIDATION_ERROR':
            title = 'Cannot Complete';
            description = errorData.details?.fields
              ? `Missing required fields: ${errorData.details.fields.join(', ')}`
              : errorData.message || 'Please fill all required fields before completing';
            break;
          case 'MISSING_INSTALLATION':
            title = 'Installation Error';
            description = 'Installation not found. Cannot complete survey.';
            break;
          case 'MISSING_CUSTOMER':
            title = 'Customer Error';
            description = 'Customer not found. Cannot complete survey.';
            break;
          default:
            description = errorData.error || errorData.message || 'Failed to complete survey';
        }
      } else if (error.message) {
        // If there's a message in the error itself
        description = error.message;
      } else {
        description = errorData?.error || errorData?.message || 'Failed to complete survey';
      }

      toast({
        title,
        description,
        variant: 'destructive',
      });
    },
  });

  const handleSave = async (data: Record<string, any>) => {
    if (!selectedInstallation) {
      // Check if this is a customer without installations
      const customerId = formData.customerId;

      if (customerId && installations?.length === 0) {
        // Customer exists but has no installations - skip saving silently
        toast({
          title: 'Cannot Save Survey',
          description: "This customer doesn't have an installation to attach the survey to.",
          variant: 'destructive',
        });
        return;
      } else {
        // No installation selected - show error
        toast({
          title: 'No Installation Selected',
          description: 'Please select an installation before saving.',
          variant: 'destructive',
        });
        return;
      }
    }

    // Merge the form data from the component with the existing formData state
    // This ensures all survey fields are included, not just what the form component passes
    const mergedData = {
      ...formData,  // Include all existing form data
      ...data,      // Override with any updates from the form component
    };

    // Preserve existing survey status if it exists - NEVER downgrade from completed
    let statusToUse = mergedData.status;
    if (!statusToUse) {
      // If existing survey is completed, keep it completed
      if (existingSurvey?.status === 'completed') {
        statusToUse = 'completed';
      } else {
        statusToUse = existingSurvey?.status || 'draft';
      }
    }

    const dataWithStatus = {
      ...mergedData,
      status: statusToUse,
    };

    console.log(
      '[SiteSurvey] Saving with status:',
      statusToUse,
      'existing status:',
      existingSurvey?.status,
      'data keys being saved:',
      Object.keys(dataWithStatus).length
    );

    await saveSurveyMutation.mutateAsync(dataWithStatus);
  };

  const handleComplete = async (data: Record<string, any>) => {
    if (!selectedInstallation) {
      // Check if this is a customer without installations
      const customerId = formData.customerId;

      if (customerId && installations?.length === 0) {
        // Customer exists but has no installations - cannot complete
        toast({
          title: 'Cannot Complete Survey',
          description: "This customer doesn't have an installation to attach the survey to.",
          variant: 'destructive',
        });
        return;
      } else {
        // No installation selected - show error
        toast({
          title: 'No Installation Selected',
          description: 'Please select an installation before completing.',
          variant: 'destructive',
        });
        return;
      }
    }

    // If offline, save with completed status
    if (!isOnline) {
      const installation = installations?.find(
        (inst) => inst.id === selectedInstallation || inst.customerId === selectedInstallation
      );
      const customerId = installation?.customer?.id || installation?.customerId || formData.customerId;

      // Merge the form data for offline save too
      const mergedDataForOffline = {
        ...formData,
        ...data,
        status: 'completed',
        completedAt: new Date().toISOString(),
      };

      keepOnDevice(customerId, mergedDataForOffline);
      setCompletedSurveyData({
        id: savedSurveyId || 0,
        customerName: installation?.customer?.name || 'Unknown Customer',
        installationAddress: installation?.installationAddress || 'Unknown Address',
      });
      setShowCompletionDialog(true);
      setUnsavedChanges(false);

      toast({
        title: 'Survey completed on this device',
        description:
          "There's no signal right now. It will upload to the office automatically when you're back online.",
      });

      return;
    }

    // Merge the form data from the component with the existing formData state
    const mergedData = {
      ...formData,  // Include all existing form data
      ...data,      // Override with any updates from the form component
    };

    await completeSurveyMutation.mutateAsync(mergedData);
  };

  // Filter installations based on search
  const filteredInstallations = installations.filter((installation: InstallationUI) => {
    const customerName = installation.customer?.name?.toLowerCase() || '';
    const address = installation.installationAddress?.toLowerCase() || '';
    const postcode = installation.customer?.postcode?.toLowerCase() || '';
    const query = searchQuery.toLowerCase();

    return customerName.includes(query) || address.includes(query) || postcode.includes(query);
  });

  // Progress calculation removed - now handled by the form component itself

  return (
    <div className="w-full">
      <PageLayout title="Site Survey">
        <div className="space-y-6">
          {/* Edit Mode Breadcrumbs */}
          {editMode && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <button
                onClick={() => window.location.href = '/survey-management'}
                className="hover:text-blue-600"
              >
                Survey Management
              </button>
              <ChevronRight className="h-4 w-4" />
              <span>Edit Survey #{editMode.surveyId}</span>
              {editMode.customerName && (
                <>
                  <ChevronRight className="h-4 w-4" />
                  <span className="font-medium">{editMode.customerName}</span>
                </>
              )}
            </div>
          )}

          {/* Loading State for Edit Mode */}
          {isLoadingEditSurvey && (
            <Card>
              <CardContent className="py-12">
                <div className="flex flex-col items-center justify-center gap-4">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                  <div className="text-center">
                    <p className="text-lg font-medium">Loading Survey for Editing</p>
                    <p className="text-sm text-gray-500 mt-1">Please wait while we load the survey data...</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          {/* Auto-save Status Bar */}
          {selectedInstallation && (
            <div className="bg-white border rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  {autoSaveStatus === 'saving' && (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                      <span className="text-sm text-blue-600">Saving...</span>
                    </>
                  )}
                  {autoSaveStatus === 'saved' && (
                    <>
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      <span className="text-sm text-green-600">All changes saved</span>
                    </>
                  )}
                  {autoSaveStatus === 'error' && (
                    <>
                      <AlertCircle className="h-4 w-4 text-red-500" />
                      <span className="text-sm text-red-600">Save failed</span>
                    </>
                  )}
                  {autoSaveStatus === 'idle' && unsavedChanges && (
                    <>
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-500">Unsaved changes</span>
                    </>
                  )}
                </div>
                {lastSaveTime && (
                  <span className="text-xs text-gray-400">
                    Last saved: {lastSaveTime.toLocaleTimeString()}
                  </span>
                )}
                {/* Edit Mode Indicator */}
                {editMode && (
                  <Badge variant="secondary" className="gap-1">
                    <Edit className="h-3 w-3" />
                    Editing Survey #{editMode.surveyId}
                  </Badge>
                )}
              </div>

              <div className="flex items-center gap-4">
                {/* Progress is now handled by the form component */}
                {/* Cache clear button - always visible for troubleshooting */}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    clearPhotoCaches();
                    setHasImageLoadError(false);
                  }}
                  disabled={clearingCache}
                  className="gap-1 text-xs"
                  title="Clear all cached data and reload"
                >
                  {clearingCache ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3 w-3" />
                  )}
                  <span className="hidden sm:inline">Clear Cache</span>
                </Button>
              </div>
            </div>
          )}

          {/* Installation Selection - Improved UI */}
          <Card className={editMode && selectedInstallation ? "opacity-75" : ""}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  {editMode && selectedInstallation ? 'Currently Editing' : 'Select Customer from Won Stage'}
                </CardTitle>
                {editMode && selectedInstallation ? (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (unsavedChanges) {
                          if (confirm('You have unsaved changes. Are you sure you want to leave?')) {
                            window.location.href = '/survey-management';
                          }
                        } else {
                          window.location.href = '/survey-management';
                        }
                      }}
                    >
                      <ArrowLeft className="h-4 w-4 mr-1" />
                      Back to Management
                    </Button>
                  </div>
                ) : (
                  <Badge variant="secondary" className="text-sm">
                    {installations.length} customers
                  </Badge>
                )}
              </div>
            </CardHeader>
            {/* Show selected customer when in edit mode */}
            {editMode && selectedInstallation && selectedInstallationDetails && (
              <CardContent>
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <User className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-blue-900">
                        {selectedInstallationDetails.customer?.name || 'Unknown Customer'}
                      </p>
                      <p className="text-sm text-blue-700">
                        {formatInstallationAddress(selectedInstallationDetails)}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditMode(null);
                      setSelectedInstallation(null);
                      window.history.replaceState({}, '', '/site-surveys');
                    }}
                  >
                    Change Customer
                  </Button>
                </div>
              </CardContent>
            )}
            
            {/* Only show content if not in edit mode with selected installation */}
            {!(editMode && selectedInstallation) && (
              <CardContent className="space-y-4">
                {/* Search Bar */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <Input
                    placeholder="Search by customer name, address, or postcode..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>

              {/* Loading State */}
              {isLoadingInstallations ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : filteredInstallations.length === 0 ? (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {searchQuery
                      ? 'No customers in Won stage matching your search.'
                      : 'No customers in Won stage found. Please ensure customers have been moved to the Won stage in the pipeline.'}
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  {/* Installation Cards Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredInstallations.map((installation: InstallationUI) => (
                      <Card
                        key={installation.id}
                        className={`cursor-pointer transition-all hover:shadow-md ${
                          selectedInstallation === installation.id
                            ? 'ring-2 ring-blue-500 bg-blue-50'
                            : 'hover:bg-gray-50'
                        }`}
                        onClick={() => handleInstallationSelect(installation.id)}
                      >
                        <CardContent className="p-4">
                          <div className="space-y-3">
                            {/* Customer Info */}
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-gray-400" />
                                <span className="font-medium">
                                  {installation.customer?.name || 'Unknown Customer'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge
                                  variant="outline"
                                  className="text-green-600 border-green-600"
                                >
                                  Won
                                </Badge>
                                {installation.installationCount &&
                                  installation.installationCount > 1 && (
                                    <Badge
                                      variant="outline"
                                      className="text-blue-600 border-blue-600"
                                    >
                                      {installation.installationCount} properties
                                    </Badge>
                                  )}
                                {installation.siteSurvey && (
                                  <Badge
                                    variant={
                                      installation.siteSurvey.status === 'completed'
                                        ? 'default'
                                        : 'secondary'
                                    }
                                  >
                                    {installation.siteSurvey.status === 'completed' ? 'Survey Complete' : 'Survey In Progress'}
                                  </Badge>
                                )}
                              </div>
                            </div>

                            {/* Address Info */}
                            <div className="space-y-1">
                              <div className="flex items-start gap-2">
                                <Home className="h-4 w-4 text-gray-400 mt-0.5" />
                                <div className="text-sm text-gray-600">
                                  <div>{formatInstallationAddress(installation)}</div>
                                </div>
                              </div>
                            </div>

                            {/* System Info */}
                            {(installation.systemSize || installation.panelCount) && (
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                {installation.systemSize && (
                                  <span>{installation.systemSize}kW System</span>
                                )}
                                {installation.panelCount && (
                                  <span>{installation.panelCount} Panels</span>
                                )}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Selected Installation Details */}
                  {selectedInstallationDetails && (
                    <Alert className="bg-blue-50 border-blue-200">
                      <CheckCircle2 className="h-4 w-4 text-blue-600" />
                      <AlertDescription>
                        <strong>Selected:</strong> {selectedInstallationDetails.customer?.name} -{' '}
                        {formatInstallationAddress(selectedInstallationDetails)}
                      </AlertDescription>
                    </Alert>
                  )}
                </>
              )}
              </CardContent>
            )}
          </Card>

          {/* Survey Form */}
          {selectedInstallation && (
            <div id="site-survey-form" className="transition-all duration-300 rounded-lg">
              {storageProblem || uploadState.state === 'needs_attention' ? (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Please check with the office</AlertTitle>
                  <AlertDescription>
                    {storageProblem ??
                      (uploadState.state === 'needs_attention' ? uploadState.message : null)}
                    {!storageProblem &&
                      uploadState.state === 'needs_attention' &&
                      uploadState.canUploadAnyway &&
                      selectedCustomerId && (
                        <div className="mt-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              if (
                                confirm(
                                  "Replace the office's copy of this survey with the version saved on this device? Changes made in the office since you went offline will be overwritten."
                                )
                              ) {
                                uploadMyVersionAnyway(selectedCustomerId);
                              }
                            }}
                          >
                            Upload my version anyway
                          </Button>
                        </div>
                      )}
                  </AlertDescription>
                </Alert>
              ) : uploadState.state === 'waiting' ? (
                <Alert className="mb-4 bg-blue-50 border-blue-200">
                  <Cloud className="h-4 w-4 text-blue-600" />
                  <AlertTitle>Waiting to upload</AlertTitle>
                  <AlertDescription>{uploadState.message}</AlertDescription>
                </Alert>
              ) : null}
              <Card className="shadow-lg">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100 border-b">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <ClipboardList className="h-6 w-6 text-blue-600" />
                      <CardTitle className="text-xl">Site Survey Form</CardTitle>
                    </div>
                    <Badge variant="outline" className="text-blue-600">
                      {selectedInstallationDetails?.customer?.name}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="site-survey-form">
                    <SiteSurveyForm
                      key={`survey-${selectedInstallation}`}
                    installationId={selectedActualInstallationId || undefined}
                    customerId={selectedCustomerId || 0}
                    surveyId={savedSurveyId || existingSurvey?.id}
                    initialData={(() => {
                      const transformed =
                        transformSurveyWithPhotos(existingSurvey) || autoPopulatedData || {};
                      console.log('[SiteSurvey] Passing initial data to form:', {
                        hasExistingSurvey: !!existingSurvey,
                        existingSurveyId: existingSurvey?.id,
                        hasAutoPopulatedData: !!autoPopulatedData,
                        transformedKeys: Object.keys(transformed),
                        panels: transformed.panels,
                        batteries: transformed.batteries,
                        batteryDescription: transformed.batteryDescription,
                        batteryQuantity: transformed.batteryQuantity,
                        sampleFields: {
                          inverterDb: transformed.inverterDb,
                          proposedArea: transformed.proposedArea,
                          dcStrings: transformed.dcStrings,
                          clientName: transformed.clientName,
                          panelsDescription: transformed.panelsDescription,
                        },
                      });
                      return transformed;
                    })()}
                    onSave={handleSave}
                    onComplete={handleComplete}
                    onChange={handleFormChange}
                    onTabChange={handleTabChange}
                    onPhotoUploaded={handlePhotoUploaded}
                    isSaving={saveSurveyMutation.isPending}
                      isCompleting={completeSurveyMutation.isPending}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Completion Dialog */}
        <SurveyCompletionDialog
          open={showCompletionDialog}
          onOpenChange={setShowCompletionDialog}
          surveyData={completedSurveyData}
        />
      </PageLayout>
    </div>
  );
}
